import { useState } from "react";
import { ShieldAlert, Upload, Star, StarHalf, CircleSlash } from "lucide-react";
import Seo from "../components/Seo";

const inputCls =
  "w-full bg-transparent border border-[#27272A] rounded-sm p-3 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none";
const labelCls = "block text-sm uppercase tracking-[0.12em] font-semibold text-[#A1A1AA] mb-2";

/**
 * OWNER MATCHING TOOL — advisory only.
 * The driver register CSV is parsed entirely in the browser: nothing is
 * uploaded, sent to any server or analytics, or stored. The page itself
 * contains no driver data — closing or refreshing it clears everything.
 * Import only the match-safe register export (no licence numbers, no
 * licence documents — see docs/DRIVER-REGISTER-GUIDE.md).
 *
 * Expected CSV headers:
 * Name, Home Area, Licence Expiry (YYYY-MM-DD), Capacity,
 * Essex to London From, Essex to London To, London to Essex From,
 * London to Essex To, Usual Days, Preferred London Areas, Status
 */

const DAY_TOKENS = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

function parseCSV(text) {
  const rows = [];
  let row = [], cell = "", inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else cell += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(cell); cell = "";
      if (row.some((v) => v.trim() !== "")) rows.push(row);
      row = [];
    } else cell += c;
  }
  if (cell !== "" || row.length) { row.push(cell); if (row.some((v) => v.trim() !== "")) rows.push(row); }
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  return rows.slice(1).map((r) => Object.fromEntries(headers.map((h, i) => [h, (r[i] || "").trim()])));
}

function toMinutes(t) {
  const m = (t || "").match(/(\d{1,2}):(\d{2})/);
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
}

function dayMatches(usualDays, dateStr) {
  if (!usualDays || !dateStr) return false;
  const day = new Date(dateStr + "T00:00:00").getDay();
  const norm = usualDays.toLowerCase();
  // Range form, e.g. "mon-fri"
  const range = norm.match(/(mon|tue|wed|thu|fri|sat|sun)\s*[-–]\s*(mon|tue|wed|thu|fri|sat|sun)/);
  if (range) {
    const a = DAY_TOKENS[range[1]], b = DAY_TOKENS[range[2]];
    if (a <= b) return day >= a && day <= b;
    return day >= a || day <= b; // wraps over weekend
  }
  return Object.entries(DAY_TOKENS).some(([tok, n]) => n === day && norm.includes(tok));
}

function normArea(s) {
  return (s || "").toLowerCase().replace(/\s*area\s*$/, "").trim();
}

function scoreDriver(d, booking) {
  const reasons = [];
  // Gate checks — only Approved drivers with valid licences and capacity.
  if ((d["status"] || "").toLowerCase() !== "approved") return null;
  const exp = new Date((d["licence expiry"] || "") + "T00:00:00");
  if (isNaN(exp) || exp < new Date(new Date().toDateString())) return null;
  if (parseInt(d["capacity"] || "0", 10) < booking.passengers) return null;

  let score = 0;
  const isE2L = booking.direction === "essex-to-london";
  const from = toMinutes(d[isE2L ? "essex to london from" : "london to essex from"]);
  const to = toMinutes(d[isE2L ? "essex to london to" : "london to essex to"]);
  const pickup = toMinutes(booking.time);

  if (from !== null && to !== null && pickup !== null) {
    if (pickup >= from && pickup <= to) {
      score += 3;
      reasons.push(`Usually travels ${isE2L ? "Essex → London" : "London → Essex"} ${d[isE2L ? "essex to london from" : "london to essex from"]}–${d[isE2L ? "essex to london to" : "london to essex to"]} — pickup time fits`);
    } else if (Math.abs(pickup - from) <= 60 || Math.abs(pickup - to) <= 60) {
      score += 1;
      reasons.push("Pickup is within an hour of their usual travel window");
    } else {
      reasons.push("Poor time match with their usual travel window");
    }
  } else {
    reasons.push("No usable travel window recorded");
  }

  const essexEnd = normArea(isE2L ? booking.fromArea : booking.toArea);
  const home = normArea(d["home area"]);
  if (essexEnd && home && essexEnd === home) {
    score += 3;
    reasons.push(`Lives in ${d["home area"]} — the Essex end of this journey`);
  } else if (essexEnd && home && (essexEnd.includes(home) || home.includes(essexEnd))) {
    score += 1;
    reasons.push(`Home area (${d["home area"]}) is near the Essex end`);
  }

  const londonEnd = normArea(isE2L ? booking.toArea : booking.fromArea);
  const prefs = (d["preferred london areas"] || "").toLowerCase();
  if (londonEnd && prefs.includes(londonEnd)) {
    score += 3;
    reasons.push(`Prefers working around the London end (${booking.isLondonLabel || "this area"})`);
  } else if (/any|all|anywhere/.test(prefs)) {
    score += 1;
    reasons.push("Works anywhere in London");
  }

  if (booking.date && dayMatches(d["usual days"], booking.date)) {
    score += 1;
    reasons.push("Usually works this day of the week");
  }

  reasons.push(`Vehicle takes ${d["capacity"]} — covers ${booking.passengers} passenger${booking.passengers > 1 ? "s" : ""}`);
  return { name: d["name"] || "Driver", home: d["home area"] || "—", score, reasons };
}

function rating(score) {
  if (score >= 7) return { label: "Strong match", Icon: Star, cls: "text-[#D4AF37]" };
  if (score >= 4) return { label: "Possible match", Icon: StarHalf, cls: "text-[#D4D4D8]" };
  return { label: "Weak match", Icon: CircleSlash, cls: "text-[#71717A]" };
}

export default function Match() {
  const [drivers, setDrivers] = useState(null);
  const [excluded, setExcluded] = useState(0);
  const [importError, setImportError] = useState("");
  const [booking, setBooking] = useState({
    direction: "london-to-essex",
    fromArea: "",
    toArea: "",
    date: "",
    time: "",
    passengers: 1,
  });
  const [results, setResults] = useState(null);

  const set = (k) => (e) => setBooking((b) => ({ ...b, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setImportError("");
    setDrivers(null);
    setResults(null);
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCSV(String(reader.result));
      if (!parsed.length || parsed[0]["status"] === undefined) {
        setImportError(
          "Could not read that file. Use the match-safe CSV export from your register (see the guide in docs/DRIVER-REGISTER-GUIDE.md)."
        );
        return;
      }
      setDrivers(parsed);
    };
    reader.readAsText(f);
  };

  const runMatch = () => {
    if (!drivers) return;
    const eligible = [];
    let excludedCount = 0;
    for (const d of drivers) {
      const scored = scoreDriver(d, { ...booking, passengers: parseInt(booking.passengers, 10) });
      if (scored) eligible.push(scored);
      else excludedCount++;
    }
    eligible.sort((a, b) => b.score - a.score);
    setResults(eligible);
    setExcluded(excludedCount);
  };

  const shareLink =
    "/job-share?" +
    new URLSearchParams({
      from: booking.fromArea,
      to: booking.toArea,
      date: booking.date,
      time: booking.time,
      passengers: String(booking.passengers),
    }).toString();

  return (
    <main data-testid="match-page" className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-20">
      <Seo title="Driver Match Tool (Owner)" description="Owner-only advisory driver matching tool." noindex />
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">Owner utility</p>
      <h1 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
        Suggest drivers for a booking
      </h1>

      <div className="mt-6 flex items-start gap-3 border border-[rgba(212,175,55,0.4)] bg-[rgba(212,175,55,0.08)] p-5" data-testid="match-privacy-note">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
        <p className="text-sm leading-relaxed text-[#A1A1AA]">
          <strong className="text-white">How access stays safe:</strong> this page holds no
          driver data at all — the register is never on the website, a server or any public
          link. You import the match-safe CSV from your private spreadsheet each time; it is
          read in your browser's memory only and disappears when you close or refresh the page.
          Anyone opening this page sees an empty tool. Suggestions are advisory — you always
          choose the driver, and nothing here contacts anyone.
        </p>
      </div>

      {/* Step 1 — import */}
      <section className="mt-10 border border-[#27272A] bg-[#0B0B0B] p-6 sm:p-8">
        <h2 className="text-xl font-semibold text-white">1 — Import your driver register</h2>
        <p className="mt-2 text-sm text-[#A1A1AA]">
          In Google Sheets: Drivers tab → File → Download → CSV, then choose it here. Never
          include licence numbers or documents in this export.
        </p>
        <label className="focus-gold mt-5 flex cursor-pointer items-center justify-center gap-3 border border-dashed border-[#3f3f46] p-6 text-sm text-[#A1A1AA] hover:border-[#D4AF37]">
          <Upload className="h-5 w-5 text-[#D4AF37]" aria-hidden="true" />
          {drivers ? `${drivers.length} drivers loaded` : "Choose register CSV…"}
          <input type="file" accept=".csv,text/csv" className="hidden" data-testid="match-csv-input" onChange={onFile} />
        </label>
        {importError && <p role="alert" data-testid="match-import-error" className="mt-3 text-sm text-red-400">{importError}</p>}
      </section>

      {/* Step 2 — booking */}
      <section className="mt-6 border border-[#27272A] bg-[#0B0B0B] p-6 sm:p-8">
        <h2 className="text-xl font-semibold text-white">2 — Booking details</h2>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="m-dir" className={labelCls}>Direction</label>
            <select id="m-dir" data-testid="match-direction" className={`${inputCls} bg-[#050505]`} value={booking.direction} onChange={set("direction")}>
              <option value="london-to-essex">London → Essex</option>
              <option value="essex-to-london">Essex → London</option>
            </select>
          </div>
          <div>
            <label htmlFor="m-passengers" className={labelCls}>Passengers</label>
            <select id="m-passengers" data-testid="match-passengers" className={`${inputCls} bg-[#050505]`} value={booking.passengers} onChange={set("passengers")}>
              {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="m-from" className={labelCls}>From (broad area)</label>
            <input id="m-from" data-testid="match-from" className={inputCls} placeholder="e.g. London Bridge / Shard area" value={booking.fromArea} onChange={set("fromArea")} />
          </div>
          <div>
            <label htmlFor="m-to" className={labelCls}>To (broad area)</label>
            <input id="m-to" data-testid="match-to" className={inputCls} placeholder="e.g. Great Dunmow area" value={booking.toArea} onChange={set("toArea")} />
          </div>
          <div>
            <label htmlFor="m-date" className={labelCls}>Journey date</label>
            <input id="m-date" data-testid="match-date" type="date" className={`${inputCls} [color-scheme:dark]`} value={booking.date} onChange={set("date")} />
          </div>
          <div>
            <label htmlFor="m-time" className={labelCls}>Pickup time</label>
            <input id="m-time" data-testid="match-time" type="time" className={`${inputCls} [color-scheme:dark]`} value={booking.time} onChange={set("time")} />
          </div>
        </div>
        <button
          type="button"
          onClick={runMatch}
          disabled={!drivers}
          data-testid="match-run-button"
          className="focus-gold mt-6 w-full border border-[#D4AF37] bg-[#D4AF37] px-6 py-4 text-sm font-semibold uppercase tracking-wider text-black transition-colors duration-150 hover:bg-[#E5C158] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {drivers ? "Suggest drivers" : "Import your register first"}
        </button>
      </section>

      {/* Step 3 — results */}
      {results && (
        <section className="mt-6 border border-[#27272A] bg-[#0B0B0B] p-6 sm:p-8" data-testid="match-results">
          <h2 className="text-xl font-semibold text-white">
            3 — Suggested drivers{" "}
            <span className="text-sm font-normal text-[#71717A]">
              ({results.length} eligible{excluded ? `, ${excluded} excluded — not approved, licence expired or too small a vehicle` : ""})
            </span>
          </h2>
          {results.length === 0 && (
            <p className="mt-4 text-sm text-[#A1A1AA]">
              No approved drivers match the gate checks. Review your register or handle this
              booking yourself.
            </p>
          )}
          <div className="mt-5 space-y-4">
            {results.map((r, i) => {
              const R = rating(r.score);
              return (
                <div key={i} className="border border-[#27272A] bg-[#111111] p-5" data-testid={`match-result-${i}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-serif text-lg font-semibold text-white">
                      {r.name} <span className="text-sm font-normal text-[#A1A1AA]">— {r.home}</span>
                    </p>
                    <p className={`flex items-center gap-1.5 text-sm font-semibold ${R.cls}`}>
                      <R.Icon className="h-4 w-4" aria-hidden="true" /> {R.label}
                    </p>
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {r.reasons.map((reason, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-[#A1A1AA]">
                        <span className="mt-2 h-px w-4 shrink-0 bg-[#D4AF37]" aria-hidden="true" />
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <p className="mt-5 text-xs leading-relaxed text-[#71717A]">
            Suggestions use only the information in your register — usual travel patterns, not
            live location or availability. Always confirm with the driver directly. When you have
            chosen, share the anonymised job via{" "}
            <a href={shareLink} data-testid="match-share-link" className="text-[#D4AF37] underline-offset-4 hover:underline">
              Job Share
            </a>
            .
          </p>
        </section>
      )}
    </main>
  );
}
